import { Link } from 'react-router-dom';
import { useSEOHead } from '../../hooks/useSEOHead';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function AboutTheOwnerPage() {
  useSEOHead({
    title: 'The Guys Behind the Madness — The Philosophy Behind Rockhound Auto',
    description: 'No resumes. No mission statements. Just the actual philosophy behind pricing transparency, the broker model, and why a car dealer gives money to dog rescues.',
    canonicalUrl: 'https://rockhoundauto.com/about-the-owner',
    robots: 'index, follow',
  });

  useEffect(() => {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'The Guys Behind the Madness',
          item: 'https://rockhoundauto.com/about-the-owner',
        },
      ],
    };

    injectJsonLd('schema-about-the-owner-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-about-the-owner-breadcrumb');
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-3 mb-6">
              <i className="ri-group-line text-3xl text-amber-400"></i>
              <span className="text-sm font-medium text-amber-400 uppercase tracking-wider">About the Owners</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              No Resumes. No Mission Statements.
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Just the actual philosophy behind how this business runs — and why it doesn't look like a normal car dealership.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-4 py-16">
          {/* Who Runs This */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Who Runs This</h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                We're not going to give you LinkedIn summaries. You don't need to know where we went to school or how many years we've been in the industry. That's not why you're here.
              </p>
              <p>
                What matters: we worked in the traditional dealership world. We saw how the money gets made. We saw the rate markup, the add-on pressure, the spot delivery tactics. We saw customers get walked through a process designed to confuse them into spending more.
              </p>
              <p>
                And we decided we didn't want to do that anymore.
              </p>
            </div>
          </section>

          {/* The Philosophy */}
          <section className="mb-16 bg-slate-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">The Philosophy</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-amber-600"></i>
                  Pricing Transparency
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  Most dealers hide the real cost until you're sitting in the finance office. By then, you're tired, you've already test-driven the car, and you just want to finish the deal. That's when they hit you with the markup.
                </p>
                <p className="text-slate-700 leading-relaxed mt-3">
                  We publish the dealer slang dictionary. We explain how rate markup works. We tell you what GAP insurance actually costs. Does that hurt our profit? Yes. Is it worth it? Also yes.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <i className="ri-handshake-line text-amber-600"></i>
                  The Broker Model
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  A broker works for you, not the dealership. That's the entire point. We don't make money by marking up your interest rate. We don't get a commission for selling you an extended warranty you don't need. We get paid to find you the right car at the right price.
                </p>
                <p className="text-slate-700 leading-relaxed mt-3">
                  It's a different business model. It makes less money per transaction. But it also means we can sleep at night.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <i className="ri-heart-line text-amber-600"></i>
                  The Dog Rescue Commitment
                </h3>
                <p className="text-slate-700 leading-relaxed">
                  This isn't a marketing strategy. We have dogs. We care about dogs more than we care about most people. Shelters are prisons for animals that didn't do anything wrong. So every car we sell puts money toward getting dogs out of those shelters.
                </p>
                <p className="text-slate-700 leading-relaxed mt-3">
                  Does it help with marketing? Probably. Does it hurt our profit? Definitely. Worth it? Very.
                </p>
                <p className="text-sm text-slate-600 mt-4">
                  <Link to="/why-the-dogs" className="text-amber-600 hover:text-amber-700 font-medium">
                    Read the full story →
                  </Link>
                </p>
              </div>
            </div>
          </section>

          {/* Dark Humor & Directness */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Dark Humor &amp; Directness as a Feature</h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                We're sarcastic. We use dark humor. We don't talk like a corporate website because we're not a corporate website. Our employees like it. Our customers seem to find it refreshing. If you don't — that's fine. There are plenty of dealerships that will speak to you in mission statements and core values.
              </p>
              <p>
                The tone you're reading right now — this is how we actually talk. It's not a brand voice. It's just our voice. If that doesn't work for you, we're probably not a good fit anyway.
              </p>
            </div>
          </section>

          {/* What This Means for You */}
          <section className="mb-16 border-l-4 border-amber-500 pl-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">What This Means for You</h2>
            <div className="space-y-3 text-slate-700 leading-relaxed">
              <p>
                <strong>If you buy a car from us:</strong> You'll know exactly what you're paying and why. No surprises in the finance office. No pressure to buy add-ons you don't need. No games.
              </p>
              <p>
                <strong>If you use broker services:</strong> We work for you. We find the car, negotiate the price, handle the paperwork. You skip the dealership entirely. You pay us a flat fee. That's it.
              </p>
              <p>
                <strong>If you just read the blog:</strong> That's fine too. Take the F&amp;I survival checklist to another dealer. Use the slang dictionary to negotiate somewhere else. We published it because it should exist, not because we expect you to buy from us.
              </p>
            </div>
          </section>

          {/* Imperfect Humans */}
          <section className="mb-16 bg-amber-50 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">We're All Imperfect</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We're not claiming to be better than anyone else. We have flaws. We've made mistakes. We're probably too blunt for our own good. We're not saints — we're just two people who decided to run a car business differently.
            </p>
            <p className="text-slate-700 leading-relaxed">
              There's good in a lot of people. There's also a lot of scars and trauma. We're all carrying something. The car industry is full of people trying to make a living, and we don't blame them for working within the system they inherited. We just chose to step outside of it.
            </p>
          </section>

          {/* Bottom Line */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">The Bottom Line</h2>
            <div className="prose prose-lg max-w-none text-slate-700 leading-relaxed space-y-4">
              <p>
                This business exists because we got tired of watching people get taken advantage of. It's structured the way it is because we wanted to build something we could actually stand behind. The transparency costs us money. The dog rescue costs us money. The broker model makes less per transaction than a traditional dealership.
              </p>
              <p>
                But we can look at ourselves in the mirror. And the customers who find us through the blog posts and the slang dictionary and the honest pricing — those are the customers we want to work with anyway.
              </p>
              <p className="font-semibold text-slate-900">
                If that sounds like the kind of car-buying experience you want, we're here. If not, no hard feelings.
              </p>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Skip the Dealership Games?</h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Browse inventory with transparent pricing, or use broker services to have us find and negotiate the car for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 text-slate-900 px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-car-line"></i>
                Browse Inventory
              </Link>
              <Link
                to="/broker-services"
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-handshake-line"></i>
                Learn About Broker Services
              </Link>
            </div>
          </section>

          {/* Related Reading */}
          <section className="mt-16 pt-8 border-t border-slate-200">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Related Reading</h3>

            {/* Blog persona cross-link */}
            <div className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6 flex flex-col md:flex-row items-center gap-5">
              <div className="flex-shrink-0 flex items-center justify-center w-14 h-14">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-2xl">
                  🧑‍🦲
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <p className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-1">Also Written By Us</p>
                <h4 className="text-lg font-bold text-slate-900 mb-1">The 2 Bald Guys Blog</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Same philosophy, different format. Dave and Mike break down dealer tactics, F&I tricks, and car-buying traps — in plain English, with zero sugarcoating.
                </p>
                <Link
                  to="/about-2-bald-guys"
                  className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm hover:text-emerald-900 transition-colors"
                >
                  <i className="ri-article-line"></i>
                  Meet the Blog Personas
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/why-the-dogs"
                className="group p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2 text-amber-600 mb-2">
                  <i className="ri-heart-line"></i>
                  <span className="font-semibold">Why the Dogs</span>
                </div>
                <p className="text-sm text-slate-600">
                  The real reason behind the rescue commitment — not marketing copy.
                </p>
              </Link>

              <Link
                to="/community-impact"
                className="group p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2 text-amber-600 mb-2">
                  <i className="ri-community-line"></i>
                  <span className="font-semibold">Community Impact</span>
                </div>
                <p className="text-sm text-slate-600">
                  How every car sale supports Oregon Dog Rescue and Puplandia.
                </p>
              </Link>
            </div>
          </section>
        </article>
      </div>
    </>
  );
}